import React, { useState, useEffect, useCallback, useRef } from 'react';
import API_BASE from './apiBase';
import { createSessionCacheKey, readSessionCache, writeSessionCache } from './sessionDataCache';
import { formatCompactNumber } from './numberFormatUtils';
import { maskEmail, maskPhone } from './maskUtils';
import "../css/App.css";

// ─── PALETTE & CONSTANTS ──────────────────────────────────────────────────────
const PALETTE = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#3b82f6',
  '#84cc16', '#a855f7', '#e11d48', '#0891b2', '#65a30d',
];
// const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const EXPIRING_SOON_DAYS = 15;

const GUIDE_STEPS = [
  { title: 'Consent Dashboard', body: "Get a bird's-eye view of all consent activity, compliance status, and trends for your organization.", target: '.base-main' },
  { title: 'Date range filter', body: 'Narrow the dashboard down to a specific date range using From and To.', target: '.tour-target-date-filter' },
  { title: 'Filter by role', body: 'Switch between All roles, Employees, Vendors & Suppliers,Partners or Customers to see role-specific data.', target: '.tour-target-role-select' },
  { title: 'Refresh', body: 'Click here to reload the latest consent data from the server.', target: '.tour-target-refresh-btn' },
  { title: 'Metric cards', body: 'Click any card (Total, Accepted, Declined, Pending, Revoked, Adults, Children, Expiring, Expired) to filter and view matching records.', target: '.tour-target-metric-cards' },
  { title: 'Regulatory compliance', body: 'Click a GDPR, DPDP, or DORA card to see a detailed pass/fail breakdown for that framework.', target: '.compliance-section' },
  { title: 'Channel & ID proof charts', body: 'Click any slice in these donut charts to view the underlying records for that channel or ID proof type.', target: '.tour-target-donut-charts' },
  { title: 'Revoked & consent-type charts', body: 'Click any bar to drill into records revoked via that channel or grouped by consent type.', target: '.tour-target-bar-charts' },
  { title: 'Monthly trend', body: 'Click any point on this chart to see consents and revocations recorded that month.', target: '.tour-target-monthly-chart' },
  { title: 'All records table', body: 'Browse every consent record here — click a row to open its full details.', target: '.tour-target-all-records' },
];

// ─── COMPLIANCE FRAMEWORK DEFINITIONS ────────────────────────────────────────
const COMPLIANCE_FRAMEWORKS = [
  {
    id: 'gdpr',
    name: 'GDPR',
    fullName: 'General Data Protection Regulation',
    region: 'India',
    color: '#3b82f6',
    lightBg: '#eff6ff',
    badgeBg: '#dbeafe',
    badgeText: '#1e40af',
    passColor: '#10b981',
    failColor: '#ef4444',
    rules: [
      {
        id: 'explicit_consent',
        label: 'Explicit consent',
        check: r => /accept|active|approved|granted/i.test(
          String(r.status || '') + String(r.user_action || '')
        ),
      },
      {
        id: 'no_expired',
        label: 'Not expired',
        check: r => {
          const d = computeDaysToExpiry(r);
          return d === null || d >= 0;
        },
      },
      {
        id: 'has_channel',
        label: 'Channel recorded',
        check: r =>
          !!(
            r.email ||
            r.phone ||
            r.app_name ||
            /email|whatsapp|sms|portal/i.test(String(r.status || '') + String(r.user_action || ''))
          ),
      },
      {
        id: 'has_type',
        label: 'Data type set',
        check: r => !!(r.type && String(r.type).trim() !== ''),
      },
    ],
  },
  {
    id: 'dpdp',
    name: 'DPDP',
    fullName: 'Digital Personal Data Protection',
    region: 'India',
    color: '#8b5cf6',
    lightBg: '#f5f3ff',
    badgeBg: '#ede9fe',
    badgeText: '#5b21b6',
    passColor: '#10b981',
    failColor: '#ef4444',
    rules: [
      {
        id: 'explicit_consent',
        label: 'Informed consent',
        check: r => /accept|active|approved|granted/i.test(
          String(r.status || '') + String(r.user_action || '')
        ),
      },
      {
        id: 'purpose_defined',
        label: 'Purpose defined',
        check: r => !!(r.type && String(r.type).trim() !== ''),
      },
      {
        id: 'not_revoked',
        label: 'Not revoked',
        check: r => !/revok/i.test(String(r.status || '')),
      },
      {
        id: 'has_identity',
        label: 'Identity verified',
        check: r => !!(r.email || r.phone),
      },
    ],
  },
  {
    id: 'dora',
    name: 'DORA',
    fullName: 'Digital Operational Resilience Act',
    region: 'India',
    color: '#f97316',
    lightBg: '#fff7ed',
    badgeBg: '#ffedd5',
    badgeText: '#9a3412',
    passColor: '#10b981',
    failColor: '#ef4444',
    rules: [
      {
        id: 'active_consent',
        label: 'Active consent',
        check: r => /accept|active|approved|granted/i.test(
          String(r.status || '') + String(r.user_action || '')
        ),
      },
      {
        id: 'entity_set',
        label: 'Entity defined',
        check: r => !!(r.entity && String(r.entity).trim() !== ''),
      },
      {
        id: 'not_expired',
        label: 'Not expired',
        check: r => {
          const d = computeDaysToExpiry(r);
          return d === null || d >= 0;
        },
      },
      {
        id: 'has_date',
        label: 'Date recorded',
        check: r => !!(r.created_at && String(r.created_at).trim() !== ''),
      },
    ],
  },
];
// ─── CHANNEL ICONS ────────────────────────────────────────────────────────────
const CHANNEL_ICONS = {
  'Email': { icon: '✉️', color: '#3b82f6' },
  'WhatsApp': { icon: '💬', color: '#10b981' },
  'SMS': { icon: '📱', color: '#f59e0b' },
  'Phone': { icon: '📞', color: '#8b5cf6' },
  'Portal': { icon: '🌐', color: '#06b6d4' },
  'Web': { icon: '🌐', color: '#06b6d4' },
  'App': { icon: '📲', color: '#f97316' },
  'Other': { icon: '🔗', color: '#94a3b8' },
};
const getChannelMeta = (ch) => {
  for (const key of Object.keys(CHANNEL_ICONS)) {
    if (String(ch).toLowerCase().includes(key.toLowerCase())) return CHANNEL_ICONS[key];
  }
  return CHANNEL_ICONS['Other'];
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const statusOf = (r) => {
  const v = String(r?.status || '').toLowerCase();
  const extra = String(r?.data || r?.consent || '').toLowerCase();
  if (/revok/.test(v)) return 'revoked';
  if (/declin|reject/.test(v)) return 'declined';
  if (/reconsent|suspend|supersed/.test(v) || /suspended_reason|suspended_at/.test(extra)) return 'reconsent';
  if (/expir|ended/.test(v)) return 'expired';
  if (/inactive|in-active|deactiv|unaccept|nonaccept|not accept|not active|not approv|not grant|not agree|disagree/.test(v)) return 'pending';
  if (/accept|active|approved|granted|agree/.test(v) || v === 'yes') return 'accepted';
  return 'pending';
};
const channelOf = (r) => {
  const s = String(r?.status || '').toLowerCase();
  const a = String(r?.user_action || '').toLowerCase();
  if (s.includes('whatsapp') || a.includes('whatsapp')) return 'WhatsApp';
  if (s.includes('sms') || a.includes('sms')) return 'SMS';
  if (s.includes('email') || a.includes('email')) return 'Email';
  if (r?.app_name) return String(r.app_name).substring(0, 18);
  if (r?.email) return 'Email';
  if (r?.phone) return 'Phone';
  return 'Other';
};
const computeDaysToExpiry = (rec) => {
  try {
    const now = new Date();
    if (rec?.expiry !== undefined && rec?.expiry !== null && rec?.expiry !== '') {
      const created = rec.created_at ? new Date(rec.created_at) : null;
      if (created && !isNaN(created)) {
        const expDate = new Date(created.getTime() + Number(rec.expiry) * 86400000);
        return Math.ceil((expDate - now) / 86400000);
      }
      return Number(rec.expiry);
    }
    if (rec?.expires_at) {
      const ed = new Date(rec.expires_at);
      if (!isNaN(ed)) return Math.ceil((ed - now) / 86400000);
    }
  } catch (_) { }
  return null;
};
const expiryInfo = (r) => {
  const days = computeDaysToExpiry(r);
  if (days === null) return { days: null, text: '—' };
  if (days < 0) return { days, text: `Expired ${Math.abs(days)}d ago` };
  if (days <= EXPIRING_SOON_DAYS) return { days, text: `Expiring (${days}d)` };
  return { days, text: `Active (${days}d)` };
};
const roleOf = (r) => {
  const src = String(r?.entity || r?.role || '').toLowerCase();
  if (/employee/.test(src)) return 'employee';
  if (/vendor/.test(src)) return 'vendor';
  if (/partner/.test(src)) return 'partner';
  if (/customer|client|buyer|consumer/.test(src)) return 'customer';
  const extra = String(r?.type || r?.consent_type || r?.category || r?.template || '').toLowerCase();
  if (/customer|client|buyer|consumer/.test(extra)) return 'customer';
  return null;
};
// const typeOf = (r) => String(r?.id_proof_type || r?.idProofType || r?.type || 'Other').trim() || 'Other';
// const typeOf = (r) => String(r?.id_type_data || r?.type || 'Other').trim() || 'Other';
const typeOf = (r) => String(r?.id_proof_type || 'Not Provided').trim() || 'Not Provided';


const getSafeBase = () => {
  try {
    const raw = String(API_BASE || '').trim();
    const first = raw.split(',')[0].trim();
    if (/^https?:\/\//.test(first) || first.startsWith('/')) return first;
    if (/^[\w.-]+(:\d+)?/.test(first)) return 'http://' + first;
    return first;
  } catch (_) { return ''; }
};

async function fetchConsents(tenantId, options = {}) {
  const base = getSafeBase();
  const qs = new URLSearchParams();
  if (tenantId) qs.set('tenant_id', String(tenantId));
  qs.set('dashboard', 'true');
  qs.set('page', String(options.page || 1));
  qs.set('page_size', String(options.pageSize || 10));
  if (options.search) qs.set('search', options.search);
  if (options.role && options.role !== 'all') qs.set('role', options.role);
  if (options.dateFrom) qs.set('date_from', options.dateFrom);
  if (options.dateTo) qs.set('date_to', options.dateTo);
  if (options.status) qs.set('dashboard_status', options.status);
  if (options.channel) qs.set('channel', options.channel);
  if (options.idProof) qs.set('id_proof', options.idProof);
  if (options.consentType) qs.set('dashboard_type', options.consentType);
  if (options.month) qs.set('month', options.month);
  if (options.expiringSoon) qs.set('expiring_soon', 'true');
  if (options.expiredOnly) qs.set('expired_only', 'true');
  if (options.childOnly) qs.set('child_only', 'true');
  if (options.includeCompliance) qs.set('include_compliance', 'true');
  if (options.includeDashboardAggregates) qs.set('include_dashboard_aggregates', 'true');
  if (options.complianceFramework) qs.set('compliance_framework', options.complianceFramework);
  if (options.complianceResult && options.complianceResult !== 'all') qs.set('compliance_result', options.complianceResult);
  const url = `${base}/user/consent?${qs.toString()}`;
  const token = localStorage.getItem('token');
  const headers = { 'Accept': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { method: 'GET', headers });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}${txt ? ': ' + txt.substring(0, 100) : ''}`);
  }
  const body = await res.json();
  const records = Array.isArray(body?.consents) ? body.consents
    : Array.isArray(body?.data) ? body.data
      : Array.isArray(body) ? body : [];
  return {
    records,
    total: Number(body?.total ?? records.length),
    page: Number(body?.page || options.page || 1),
    pageSize: Number(body?.page_size || options.pageSize || 10),
    totalPages: Number(body?.total_pages || 1),
    stats: body?.stats || {},
    filteredStats: body?.filtered_stats || body?.stats || {},
    aggregates: body?.aggregates || null,
  };
}

// ─── MINI COMPONENTS ─────────────────────────────────────────────────────────
const NavBtn = ({ onClick, disabled, label }) => (
  <button onClick={onClick} disabled={disabled} style={{
    width: 28, height: 28, borderRadius: 7, border: '1.5px solid #e2e8f0',
    background: '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    opacity: disabled ? 0.3 : 1, fontSize: 16, color: '#475569',
  }}>{label}</button>
);

const StatusBadge = ({ status }) => {
  const s = String(status || '').toLowerCase();
  if (/accept/.test(s)) return <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#15803d' }}>✅ Accepted</span>;
  if (/declin|reject/.test(s)) return <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#b91c1c' }}>❌ Declined</span>;
  if (/reconsent|suspend|supersed|expired|ended/.test(s)) return <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#b45309' }}>🔁 Reconsent</span>;
  if (/revok/.test(s)) return <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#ffedd5', color: '#c2410c' }}>🔄 Revoked</span>;
  return <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#fef9c3', color: '#a16207' }}>⏳ Pending</span>;
};

const ChannelBadge = ({ channel }) => {
  const meta = getChannelMeta(channel);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
      background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}30`,
    }}>
      <span style={{ fontSize: 12 }}>{meta.icon}</span>{channel}
    </span>
  );
};

// ─── MODAL ────────────────────────────────────────────────────────────────────
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const maxW = size === 'lg' ? 860 : size === 'sm' ? 420 : 620;
  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.55)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '36px 16px', overflowY: 'auto',
    }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: maxW, border: '1px solid #e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid #f1f5f9' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: '#94a3b8', fontSize: 17 }}>✕</button>
        </div>
        <div style={{ padding: '18px 22px', maxHeight: '72vh', overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
};

// ─── DONUT CHART ─────────────────────────────────────────────────────────────
const DonutChart = ({ data, title, pageSize = 5, onSliceClick }) => {
  const [page, setPage] = useState(0);
  const [hovered, setHovered] = useState(null);
  useEffect(() => { setPage(0); setHovered(null); }, [data.length, title]);
  if (!data.length) return <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 28 }}>No data</div>;
  const totalPages = Math.ceil(data.length / pageSize);
  const visibleItems = data.slice(page * pageSize, page * pageSize + pageSize);
  const grandTotal = data.reduce((s, d) => s + d.value, 0) || 1;
  const size = 160, cx = size / 2, cy = size / 2, R = 66, r = 43;
  let angle = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const sweep = (d.value / grandTotal) * 2 * Math.PI;
    const a1 = angle, a2 = angle + sweep;
    angle = a2;
    const mid = (a1 + a2) / 2, exp = hovered === i ? 5 : 0;
    const ox = Math.cos(mid) * exp, oy = Math.sin(mid) * exp;
    const x1 = cx + ox + R * Math.cos(a1), y1 = cy + oy + R * Math.sin(a1);
    const x2 = cx + ox + R * Math.cos(a2), y2 = cy + oy + R * Math.sin(a2);
    const x3 = cx + ox + r * Math.cos(a2), y3 = cy + oy + r * Math.sin(a2);
    const x4 = cx + ox + r * Math.cos(a1), y4 = cy + oy + r * Math.sin(a1);
    const lg = sweep > Math.PI ? 1 : 0;
    return { ...d, i, path: `M${x1},${y1} A${R},${R},0,${lg},1,${x2},${y2} L${x3},${y3} A${r},${r},0,${lg},0,${x4},${y4} Z` };
  });

  const hov = hovered !== null ? slices[hovered] : null;
  const rawDisplayVal = hov ? hov.value : grandTotal;
  const displayVal = formatCompactNumber(rawDisplayVal);
  const valFontSize = displayVal.length > 7 ? 13 : displayVal.length > 5 ? 15 : 18;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <svg width={size} height={size} style={{ flexShrink: 0, overflow: 'visible', cursor: 'pointer', margin: '0 auto' }}>
          {slices.map(s => (
            <path key={s.i} d={s.path} fill={s.color}
              opacity={hovered === null || hovered === s.i ? 1 : 0.25}
              style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
              onMouseEnter={() => setHovered(s.i)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSliceClick && onSliceClick(s)} />
          ))}
          <circle cx={cx} cy={cy} r={r - 1} fill="#ffffff" />
          <text x={cx} y={cy - (hov ? 8 : 4)} textAnchor="middle" style={{ fontSize: valFontSize, fontWeight: 800, fill: '#0f172a', fontFamily: 'inherit' }}>
            <title>{Number(rawDisplayVal).toLocaleString()}</title>
            {displayVal}
          </text>
          <text x={cx} y={cy + (hov ? 8 : 12)} textAnchor="middle" style={{ fontSize: 9.5, fontWeight: 600, fill: '#64748b', fontFamily: 'inherit' }}>
            {hov ? hov.name.substring(0, 14) : title}
          </text>
          {hov && (
            <text x={cx} y={cy + 22} textAnchor="middle" style={{ fontSize: 10, fontWeight: 700, fill: hov.color, fontFamily: 'inherit' }}>
              {Math.round((hov.value / grandTotal) * 100)}%
            </text>
          )}
        </svg>

        <div style={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          {visibleItems.map(d => {
            const ri = data.findIndex(x => x.name === d.name);
            const meta = getChannelMeta(d.name);
            const isChannel = title === 'Channels';
            const pct = Math.round((d.value / grandTotal) * 100);
            return (
              <div key={d.name}
                onMouseEnter={() => setHovered(ri)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onSliceClick && onSliceClick(d)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                  padding: '3px 6px', borderRadius: 6,
                  background: hovered === ri ? `${d.color}18` : 'transparent',
                  opacity: hovered === null || hovered === ri ? 1 : 0.4,
                  transition: 'all 0.12s',
                }}>
                {isChannel
                  ? <span style={{ fontSize: 14, flexShrink: 0 }}>{meta.icon}</span>
                  : <span style={{ width: 9, height: 9, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                }
                <span style={{ fontSize: 11, color: '#475569', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.name}>{d.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }} title={Number(d.value).toLocaleString()}>
                  {formatCompactNumber(d.value)} <span style={{ color: d.color }}>({Math.round((d.value / grandTotal) * 100)}%)</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <NavBtn onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} label="‹" />
          <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 48, textAlign: 'center' }}>{page + 1}/{totalPages}</span>
          <NavBtn onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} label="›" />
        </div>
      )}
    </div>
  );
};

// ─── HORIZONTAL BAR CHART ────────────────────────────────────────────────────
const HBarChart = ({ data, pageSize = 5, onBarClick }) => {
  const [page, setPage] = useState(0);
  const [hovered, setHovered] = useState(null);
  useEffect(() => { setPage(0); setHovered(null); }, [data.length]);
  if (!data.length) return <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 28 }}>No data</div>;
  const totalPages = Math.ceil(data.length / pageSize);
  const visible = data.slice(page * pageSize, page * pageSize + pageSize);
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {visible.map((d, i) => {
        const meta = getChannelMeta(d.name);
        return (
          <div key={d.name}
            onClick={() => onBarClick && onBarClick(d)}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, alignItems: 'center' }}>
              <span title={d.name} style={{ fontSize: 12, color: '#475569', fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{meta.icon}</span>{d.name}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: d.color }} title={Number(d.value).toLocaleString()}>{formatCompactNumber(d.value)}</span>
            </div>
            <div style={{ height: 10, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${Math.round((d.value / max) * 100)}%`,
                background: d.color, borderRadius: 99,
                opacity: hovered === null || hovered === i ? 1 : 0.3,
                transition: 'width 0.55s ease, opacity 0.12s',
              }} />
            </div>
          </div>
        );
      })}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <NavBtn onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} label="‹" />
          <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 48, textAlign: 'center' }}>{page + 1}/{totalPages}</span>
          <NavBtn onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} label="›" />
        </div>
      )}
    </div>
  );
};

export const buildMonthlyHistoryPoints = ({ data, chartWidth = 600, chartHeight = 220, padding = { top: 18, right: 18, bottom: 36, left: 42 }, maxValue }) => {
  const safeData = Array.isArray(data) ? data : [];
  const safeMax = Number(maxValue) > 0 ? Number(maxValue) : Math.max(...safeData.map((d) => Number(d?.value) || 0), 1);
  const plotWidth = Math.max(chartWidth - padding.left - padding.right, 1);
  const plotHeight = Math.max(chartHeight - padding.top - padding.bottom, 1);
  const stepX = safeData.length > 1 ? plotWidth / (safeData.length - 1) : 0;

  return safeData.map((item, index) => {
    const value = Number(item?.value) || 0;
    const x = padding.left + (safeData.length > 1 ? index * stepX : plotWidth / 2);
    const y = padding.top + plotHeight - (value / safeMax) * plotHeight;
    return { ...item, value, x, y, maxValue: safeMax };
  });
};

// ─── MONTHLY CHART ────────────────────────────────────────────────────────────
const MonthlyChart = ({ consentData, revokedData, onBarClick }) => {
  const [hovered, setHovered] = useState(null);
  const chartWidth = 620;
  const chartHeight = 220;
  const padding = { top: 18, right: 18, bottom: 42, left: 42 };
  const max = Math.max(...consentData.map(d => Number(d.value) || 0), ...revokedData.map(d => Number(d.value) || 0), 1);
  const consentPoints = buildMonthlyHistoryPoints({ data: consentData, chartWidth, chartHeight, padding, maxValue: max });
  const revokedPoints = buildMonthlyHistoryPoints({ data: revokedData, chartWidth, chartHeight, padding, maxValue: max });
  const linePath = (points) => points.map((p, index) => `${index === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
  const areaPath = (points) => {
    if (!points.length) return '';
    const first = points[0];
    const last = points[points.length - 1];
    return `${linePath(points)} L ${last.x.toFixed(2)} ${chartHeight - padding.bottom} L ${first.x.toFixed(2)} ${chartHeight - padding.bottom} Z`;
  };
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
        {[['#6366f1', 'Consents'], ['#f97316', 'Revoked']].map(([c, l]) => (
          <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b' }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: c, display: 'inline-block' }} />{l}
          </span>
        ))}
        <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>📈 History view</span>
      </div>
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} width="100%" height={chartHeight} role="img" aria-label="Monthly consents and revoked history chart">
        {yTicks.map((tick) => {
          const y = padding.top + (chartHeight - padding.top - padding.bottom) * (1 - tick);
          const value = Math.round(max * tick);
          return (
            <g key={tick}>
              <line x1={padding.left} x2={chartWidth - padding.right} y1={y} y2={y} stroke="#e2e8f0" strokeDasharray="4 3" />
              <text x={8} y={y + 4} fontSize="10" fill="#94a3b8">{formatCompactNumber(value)}</text>
            </g>
          );
        })}
        <line x1={padding.left} x2={padding.left} y1={padding.top} y2={chartHeight - padding.bottom} stroke="#64748b" strokeWidth="1" />
        <line x1={padding.left} x2={chartWidth - padding.right} y1={chartHeight - padding.bottom} y2={chartHeight - padding.bottom} stroke="#64748b" strokeWidth="1" />
        <path d={areaPath(consentPoints)} fill="rgba(99, 102, 241, 0.12)" />
        <path d={areaPath(revokedPoints)} fill="rgba(249, 115, 22, 0.12)" />
        <path d={linePath(consentPoints)} fill="none" stroke="#6366f1" strokeWidth="2.4" strokeLinecap="round" />
        <path d={linePath(revokedPoints)} fill="none" stroke="#f97316" strokeWidth="2.4" strokeLinecap="round" />
        {consentPoints.map((point, index) => {
          const rv = revokedPoints[index]?.value || 0;
          const isActive = hovered === index;
          return (
            <g key={`${point.key || point.month}-${index}`}>
              <circle
                cx={point.x}
                cy={point.y}
                r={isActive ? 6 : 4.5}
                fill="#6366f1"
                stroke="#fff"
                strokeWidth="1.5"
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onBarClick && onBarClick({ month: point.month, consents: point.value, revoked: rv, key: point.key })}
                style={{ cursor: 'pointer' }}
              />
              <circle
                cx={revokedPoints[index].x}
                cy={revokedPoints[index].y}
                r={isActive ? 6 : 4.5}
                fill="#f97316"
                stroke="#fff"
                strokeWidth="1.5"
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onBarClick && onBarClick({ month: point.month, consents: point.value, revoked: rv, key: point.key })}
                style={{ cursor: 'pointer' }}
              />
              {index === consentPoints.length - 1 && (
                <text x={point.x} y={chartHeight - 16} textAnchor="middle" fontSize="10" fill="#64748b">{point.month}</text>
              )}
            </g>
          );
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: padding.left, paddingRight: padding.right, marginTop: 6 }}>
        {consentPoints.map((point) => (
          <span key={point.key || point.month} style={{ fontSize: 10, color: '#64748b', textAlign: 'center', minWidth: 36 }}>{point.month}</span>
        ))}
      </div>
    </div>
  );
};

// ─── RECORD TABLE ─────────────────────────────────────────────────────────────
const RecordTable = ({ records, onRowClick, perPage = 10, page = 0, serverPaged = false }) => {
  const visible = serverPaged ? records : records.slice(page * perPage, page * perPage + perPage);
  return (
    <div>
      <div style={{ borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['S.No', 'User', 'Type', 'Status', 'Channel', 'Entity', 'Date'].map(h => (
                <th key={h} style={{ padding: '9px 13px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No records found</td></tr>
            )}
            {visible.map((r, i) => (
              <tr key={r.id || i}
                onClick={() => onRowClick && onRowClick(r)}
                style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer', background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa')}>
                <td style={{ padding: '9px 13px', fontWeight: 500, color: '#64748b' }}>
                  {page * perPage + i + 1}
                </td>
                <td style={{ padding: '9px 13px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#eff6ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                      {String(r.username || r.email || '?').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 12 }}>{r.username || '—'}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{maskEmail(r.email) || ''}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '9px 13px', color: '#475569', maxWidth: 140 }}>
                  <span title={r.type || '—'} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.type || '—'}</span>
                </td>
                <td style={{ padding: '9px 13px' }}><StatusBadge status={r.status} /></td>
                <td style={{ padding: '9px 13px' }}><ChannelBadge channel={channelOf(r)} /></td>
                <td style={{ padding: '9px 13px', color: '#64748b', fontSize: 12 }}>{r.entity || '—'}</td>
                <td style={{ padding: '9px 13px', color: '#94a3b8', fontSize: 11 }}>{r.created_at ? String(r.created_at).split('T')[0] : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── RECORD DETAIL ────────────────────────────────────────────────────────────
const RecordDetail = ({ r }) => {
  const ei = expiryInfo(r);
  const initials = String(r.username || r.email || '?')
    .split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  const complianceResults = COMPLIANCE_FRAMEWORKS.map(fw => ({
    ...fw,
    rules: fw.rules.map(rule => ({ ...rule, passed: rule.check(r) })),
    allPassed: fw.rules.every(rule => rule.check(r)),
  }));
  const encConsentId = r.encrypted_id || r.public_id || (r.id ? `CNS-${String(r.id).padStart(8, '0')}` : null);
  const fields = [
    ['Consent Ref', encConsentId],
    ['Full name', r.username], ['Email', maskEmail(r.email)], ['Phone', maskPhone(r.phone)],
    ['Date of birth', r.dob], ['Entity / Role', r.entity], ['Consent type', r.type],
    ['Channel', channelOf(r)], ['App', r.app_name],
    ['Created', r.created_at ? String(r.created_at).split('T')[0] : null],
    ['Expiry', ei.text], ['Department', r.department],
    ['Parent name', r.parent_name], ['Parent email', maskEmail(r.parent_email)],
  ].filter(([, v]) => v !== undefined && v !== null && v !== '');

  const status = statusOf(r);
  const statusStyle = {
    accepted: { bg: '#dcfce7', color: '#15803d', text: '✅ Accepted' },
    declined: { bg: '#fee2e2', color: '#b91c1c', text: '❌ Declined' },
    revoked: { bg: '#ffedd5', color: '#c2410c', text: '🔄 Revoked' },
    reconsent: { bg: '#fef3c7', color: '#b45309', text: '🔁 Reconsent' },
    pending: { bg: '#fef9c3', color: '#a16207', text: '⏳ Pending' },
  }[status] || { bg: '#f1f5f9', color: '#64748b', text: status };

  return (
    <div>
      {/* Hero Card */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px', background: 'linear-gradient(135deg,#eff6ff,#f5f3ff)',
        borderRadius: 12, marginBottom: 16, border: '1px solid #dbeafe',
      }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%',
          background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
          color: '#fff', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 18, fontWeight: 700, flexShrink: 0,
        }}>{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>
            {r.username || '—'}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
            {[maskEmail(r.email), maskPhone(r.phone)].filter(Boolean).join('  ·  ')}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: statusStyle.bg, color: statusStyle.color }}>
              {statusStyle.text}
            </span>
            <ChannelBadge channel={channelOf(r)} />
            {r.type && (
              <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: '#ede9fe', color: '#5b21b6' }}>
                📋 {r.type}
              </span>
            )}
            {ei.days !== null && ei.days >= 0 && ei.days <= EXPIRING_SOON_DAYS && (
              <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: '#fef9c3', color: '#a16207' }}>
                ⏰ {ei.text}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Status + Expiry boxes */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        <div style={{ background: statusStyle.bg, borderRadius: 10, padding: '12px 14px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: statusStyle.color }}>{statusStyle.text}</div>
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>Status</div>
        </div>
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
          <span style={{
            fontSize: 13, fontWeight: 700,
            color: ei.days === null ? '#94a3b8' : ei.days < 0 ? '#b91c1c' : ei.days <= EXPIRING_SOON_DAYS ? '#a16207' : '#15803d',
          }}>{ei.text}</span>
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>Expiry</div>
        </div>
      </div>

      {/* Field rows */}
      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>Details</div>
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
        {fields.map(([label, val], i) => (
          <div key={label} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '9px 14px', background: i % 2 === 0 ? '#fff' : '#fafafa',
            borderTop: i > 0 ? '1px solid #f1f5f9' : 'none', gap: 16,
          }}>
            <span style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', flexShrink: 0 }}>{label}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', textAlign: 'right', wordBreak: 'break-all' }}>
              {label === 'Channel' ? <ChannelBadge channel={String(val)} /> : String(val)}
            </span>
          </div>
        ))}
      </div>

      {/* Compliance check */}
      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>Compliance</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {complianceResults.map(fw => (
          <div key={fw.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '9px 12px', borderRadius: 9,
            background: fw.lightBg, border: `1px solid ${fw.color}30`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: fw.color }}>{fw.name}</span>
              <span style={{
                padding: '1px 7px', borderRadius: 99, fontSize: 9, fontWeight: 700,
                background: fw.allPassed ? '#dcfce7' : '#fee2e2',
                color: fw.allPassed ? '#15803d' : '#b91c1c',
              }}>{fw.allPassed ? 'PASS' : 'FAIL'}</span>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {fw.rules.map(rule => (
                <span key={rule.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  padding: '1px 7px', borderRadius: 99, fontSize: 10, fontWeight: 600,
                  background: rule.passed ? '#dcfce7' : '#fee2e2',
                  color: rule.passed ? '#15803d' : '#b91c1c',
                }}>
                  {rule.passed ? '✅' : '❌'} {rule.label}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ─── METRIC CARD ──────────────────────────────────────────────────────────────
const MetricCard = ({ icon, label, value, color, bg, onClick, active }) => (
  <div onClick={onClick} title={Number(value || 0).toLocaleString()} style={{
    background: '#fff', border: active ? `2px solid ${color}` : '1.5px solid #e2e8f0',
    borderRadius: 14, padding: '13px 11px', cursor: 'pointer',
    boxShadow: active ? `0 0 0 3px ${color}20` : '0 1px 3px rgba(0,0,0,0.04)',
    transition: 'all 0.14s', userSelect: 'none',
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; if (!active) e.currentTarget.style.borderColor = color; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; if (!active) e.currentTarget.style.borderColor = '#e2e8f0'; }}>
    <div style={{ width: 32, height: 32, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 9, fontSize: 15 }}>{icon}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', lineHeight: 1 }} title={Number(value || 0).toLocaleString()}>{formatCompactNumber(value)}</div>
    <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>{label}</div>
  </div>
);

const ChartCard = ({ title, children, hint, onClick }) => (
  <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14, padding: 18 }}>
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
      }}
      title={hint || 'Click to view records'}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</div>
      {hint && <span style={{ fontSize: 10, color: '#94a3b8' }}>{hint}</span>}
    </div>
    {children}
  </div>
);

// ─── CHART POPUP CONTENT ──────────────────────────────────────────────────────
const ChartPopupContent = ({ records, defaultStatus, stats, page, pageSize = 8, total, loading, onStatusChange, onPageChange, onRowClick }) => {
  const serverPaged = typeof onStatusChange === 'function';
  const normalizedDefaultStatus = defaultStatus ? String(defaultStatus).toLowerCase() : null;
  const [activeStatus, setActiveStatus] = useState(normalizedDefaultStatus);
  const a = serverPaged ? Number(stats?.accepted ?? stats?.active ?? 0) : records.filter(r => statusOf(r) === 'accepted').length;
  const d = serverPaged ? Number(stats?.declined ?? stats?.rejected ?? 0) : records.filter(r => statusOf(r) === 'declined').length;
  const rv = serverPaged ? Number(stats?.revoked ?? 0) : records.filter(r => statusOf(r) === 'revoked').length;
  const [selectedRecord, setSelectedRecord] = useState(null);
  const detailRef = useRef(null);

  useEffect(() => {
    setActiveStatus(normalizedDefaultStatus);
    setSelectedRecord(null);
  }, [normalizedDefaultStatus]);

  const resultTotal = serverPaged ? Number(total || 0) : records.length;
  const reconsentCount = serverPaged ? Number(stats?.reconsent ?? 0) : records.filter(r => statusOf(r) === 'reconsent').length;
  const p = serverPaged ? Number(stats?.pending ?? 0) : Math.max(0, records.length - a - d - rv - reconsentCount);
  const effectiveStatus = normalizedDefaultStatus || activeStatus;
  const filteredRecords = serverPaged ? records : (effectiveStatus ? records.filter(r => statusOf(r) === effectiveStatus) : records);
  const statusCards = [
    { label: '✅ Accepted', value: a, status: 'accepted', bg: '#dcfce7', color: '#15803d', activeBg: '#bbf7d0', border: '#86efac' },
    { label: '❌ Declined', value: d, status: 'declined', bg: '#fee2e2', color: '#b91c1c', activeBg: '#fecaca', border: '#fca5a5' },
    { label: '🔁 Reconsent', value: reconsentCount, status: 'reconsent', bg: '#fef3c7', color: '#b45309', activeBg: '#fde68a', border: '#f59e0b' },
    { label: '🔄 Revoked', value: rv, status: 'revoked', bg: '#ffedd5', color: '#c2410c', activeBg: '#fed7aa', border: '#fdba74' },
    { label: '⏳ Pending', value: p, status: 'pending', bg: '#fef9c3', color: '#a16207', activeBg: '#fef08a', border: '#fde047' },
  ];
  const statusLabel = {
    accepted: 'Accepted',
    declined: 'Declined',
    reconsent: 'Reconsent',
    revoked: 'Revoked',
    pending: 'Pending',
  }[effectiveStatus] || effectiveStatus;

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 14 }}>
        {statusCards.map(({ label, value, status, bg, color, activeBg, border }) => {
          const isActive = effectiveStatus === status;
          return (
            <div key={status} onClick={() => {
              const nextStatus = isActive ? null : status;
              setActiveStatus(nextStatus);
              if (serverPaged) onStatusChange(nextStatus);
            }}
              style={{
                background: isActive ? activeBg : bg, borderRadius: 10, padding: '12px 10px',
                textAlign: 'center', cursor: 'pointer',
                border: isActive ? `2px solid ${border}` : '2px solid transparent',
                boxShadow: isActive ? `0 0 0 3px ${color}20` : 'none',
                transition: 'all 0.15s', userSelect: 'none',
                transform: isActive ? 'translateY(-2px)' : 'none',
              }}>
              <div style={{ fontSize: 22, fontWeight: 700, color }} title={Number(value || 0).toLocaleString()}>{formatCompactNumber(value)}</div>
              <div style={{ fontSize: 11, color, marginTop: 3, fontWeight: 600 }}>{label}</div>
              {isActive && <div style={{ fontSize: 10, color, marginTop: 4, opacity: 0.7 }}>● Filtering</div>}
            </div>
          );
        })}
      </div>
      {effectiveStatus && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', marginBottom: 10, background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 8, fontSize: 12 }}>
          <span style={{ color: '#1d4ed8', fontWeight: 600 }}>
            🔍 Showing: <strong style={{ textTransform: 'capitalize' }}>{statusLabel}</strong> — <span title={Number(serverPaged ? resultTotal : filteredRecords.length).toLocaleString()}>{formatCompactNumber(serverPaged ? resultTotal : filteredRecords.length)} records</span>
          </span>
          <button onClick={() => { setActiveStatus(null); if (serverPaged) onStatusChange(null); }} style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>Clear ✕</button>
        </div>
      )}
      {selectedRecord && (
        <div ref={detailRef} style={{ marginBottom: 14, border: '1.5px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              📄 {selectedRecord.username || maskEmail(selectedRecord.email) || 'Record Detail'}
            </span>
            <button onClick={() => setSelectedRecord(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '2px 6px', borderRadius: 6 }}>✕ Close</button>
          </div>
          <div style={{ padding: '14px 16px' }}>
            <RecordDetail r={selectedRecord} />
          </div>
        </div>
      )}
      {loading && <div style={{ padding: 12, textAlign: 'center', color: '#64748b', fontSize: 12 }}>Loading records…</div>}
      <RecordTable records={filteredRecords} perPage={pageSize} page={page || 0} serverPaged={serverPaged} onRowClick={r => {
        setSelectedRecord(r);
        if (onRowClick) onRowClick(r);
        setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      }} />
      {serverPaged && total > pageSize && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12 }}>
          <NavBtn onClick={() => onPageChange(Math.max(0, page - 1))} disabled={page === 0 || loading} label="‹" />
          <span style={{ fontSize: 11, color: '#64748b' }}>
            Page {page + 1} of {Math.ceil(total / pageSize)} · <span title={Number(total).toLocaleString()}>{formatCompactNumber(total)} records</span>
          </span>
          <NavBtn onClick={() => onPageChange(Math.min(Math.ceil(total / pageSize) - 1, page + 1))} disabled={page >= Math.ceil(total / pageSize) - 1 || loading} label="›" />
        </div>
      )}
      {/* <RecordTable records={filteredRecords} perPage={8} onRowClick={r => setSelectedRecord(r)} /> */}
    </>
  );
};

const PagedMetricContent = ({ tenantId, filter, role, dateFrom, dateTo, search, initialStats, initialTotal }) => {
  const initialStatus = filter?.status || (filter?.type === 'status' ? String(filter.value).toLowerCase() : null);
  const [status, setStatus] = useState(initialStatus);
  const [page, setPage] = useState(0);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(initialTotal || 0);
  const [stats, setStats] = useState(initialStats || {});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pageSize = 8;

  const channel = filter?.type === 'channel' ? filter.value : null;
  const idProof = filter?.type === 'id_proof' ? filter.value : null;
  const consentType = filter?.type === 'consent_type' ? filter.value : null;
  const month = filter?.type === 'month' ? filter.value : null;
  const expiringSoon = filter?.type === 'expiringSoon';
  const expiredOnly = filter?.type === 'expired';
  const childOnly = filter?.type === 'child';

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    fetchConsents(tenantId, {
      page: page + 1,
      pageSize,
      role,
      dateFrom,
      dateTo,
      search,
      status,
      channel,
      idProof,
      consentType,
      month,
      expiringSoon,
      expiredOnly,
      childOnly,
    }).then(result => {
      if (!current) return;
      setRecords(result.records);
      setTotal(result.total);
      setStats(result.filteredStats || result.stats || {});
    }).catch(err => {
      if (current) setError(String(err.message || err));
    }).finally(() => {
      if (current) setLoading(false);
    });
    return () => { current = false; };
  }, [tenantId, page, role, dateFrom, dateTo, search, status, channel, idProof, consentType, month, expiringSoon, expiredOnly, childOnly]);

  return (
    <>
      {error && <div style={{ color: '#b91c1c', fontSize: 12, padding: 10 }}>Failed to load records: {error}</div>}
      <ChartPopupContent
        records={records}
        defaultStatus={initialStatus}
        stats={stats}
        page={page}
        pageSize={pageSize}
        total={total}
        loading={loading}
        onStatusChange={nextStatus => { setStatus(nextStatus); setPage(0); }}
        onPageChange={setPage}
      />
    </>
  );
};


// ─── COMPLIANCE MINI SPARKLINE ────────────────────────────────────────────────
const MiniSparkline = ({ data, color }) => {
  if (!data || data.length === 0) return null;
  const W = 220, H = 38;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (W - 8) + 4;
    const y = H - 4 - ((v / max) * (H - 12));
    return `${x},${y}`;
  }).join(' ');
  const areaEnd = `${(data.length - 1) / (data.length - 1) * (W - 8) + 4},${H - 4} 4,${H - 4}`;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={`${pts} ${areaEnd}`} fill={`url(#sg-${color.replace('#', '')})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {data.map((v, i) => {
        const x = (i / (data.length - 1)) * (W - 8) + 4;
        const y = H - 4 - ((v / max) * (H - 12));
        return i === data.length - 1 ? <circle key={i} cx={x} cy={y} r={3} fill={color} /> : null;
      })}
    </svg>
  );
};

// ─── COMPLIANCE MODAL CONTENT ─────────────────────────────────────────────────

const ComplianceModalContent = ({ framework, records, stats, tenantId, role, dateFrom, dateTo, search }) => {
  const [filter, setFilter] = useState('all'); // 'all' | 'pass' | 'fail'
  const [page, setPage] = useState(1);
  const [pageRecords, setPageRecords] = useState(records);
  const [pageTotal, setPageTotal] = useState(Number(stats?.total ?? records.length));
  const [pageLoading, setPageLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const detailRef = useRef(null);

  useEffect(() => {
    if (!tenantId) return undefined;
    let current = true;
    setPageLoading(true);
    fetchConsents(tenantId, {
      page,
      pageSize: 20,
      role,
      dateFrom,
      dateTo,
      search,
      complianceFramework: framework.id,
      complianceResult: filter,
    }).then(result => {
      if (!current) return;
      setPageRecords(result.records);
      setPageTotal(result.total);
    }).finally(() => {
      if (current) setPageLoading(false);
    });
    return () => { current = false; };
  }, [tenantId, page, role, dateFrom, dateTo, search, framework.id, filter]);

  const recordsForPage = tenantId ? pageRecords : records;

  const evaluated = recordsForPage.map(r => {
    const ruleResults = framework.rules.map(rule => ({
      ...rule,
      passed: rule.check(r),
    }));
    const passed = ruleResults.every(rr => rr.passed);
    return { record: r, ruleResults, passed };
  });

  const totalCount = Number(stats?.total ?? evaluated.length);
  const passCount = Number(stats?.passed ?? evaluated.filter(e => e.passed).length);
  const failCount = Number(stats?.failed ?? Math.max(0, totalCount - passCount));
  const rate = totalCount > 0 ? Math.round((passCount / totalCount) * 100) : 0;

  const ruleBreakdown = framework.rules.map(rule => {
    const ruleStats = stats?.rules?.[rule.id];
    const passedCount = Number(ruleStats?.passed ?? evaluated.filter(e =>
      e.ruleResults.find(rr => rr.id === rule.id)?.passed
    ).length);
    return { ...rule, passedCount, failedCount: Number(ruleStats?.failed ?? Math.max(0, totalCount - passedCount)) };
  });

  const shown = tenantId ? evaluated : evaluated.filter(e => {
    if (filter === 'pass') return e.passed;
    if (filter === 'fail') return !e.passed;
    return true;
  });

  // Stat card definitions — now with click metadata
  const statCards = [
    {
      key: 'all',
      label: 'Total Records',
      val: totalCount,
      bg: '#f8fafc',
      activeBg: '#e2e8f0',
      color: '#475569',
      border: '#94a3b8',
    },
    {
      key: 'pass',
      label: '✅ Passed',
      val: passCount,
      bg: '#dcfce7',
      activeBg: '#bbf7d0',
      color: '#15803d',
      border: '#86efac',
    },
    {
      key: 'fail',
      label: '❌ Failed',
      val: failCount,
      bg: '#fee2e2',
      activeBg: '#fecaca',
      color: '#b91c1c',
      border: '#fca5a5',
    },
  ];

  return (
    <div>
      {/* ── Clickable stat cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 18 }}>
        {statCards.map(({ key, label, val, bg, activeBg, color, border }) => {
          const isActive = filter === key;
          return (
            <div
              key={key}
              onClick={() => setFilter(isActive ? 'all' : key)}
              title={`Click to filter by ${label}`}
              style={{
                background: isActive ? activeBg : bg,
                borderRadius: 10,
                padding: '14px 12px',
                textAlign: 'center',
                cursor: 'pointer',
                border: isActive ? `2px solid ${border}` : '2px solid transparent',
                boxShadow: isActive ? `0 0 0 3px ${color}20` : 'none',
                transition: 'all 0.15s',
                userSelect: 'none',
                transform: isActive ? 'translateY(-2px)' : 'none',
              }}
              onMouseEnter={e => {
                if (!isActive) e.currentTarget.style.background = activeBg;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                if (!isActive) e.currentTarget.style.background = bg;
                e.currentTarget.style.transform = isActive ? 'translateY(-2px)' : 'none';
              }}
            >
              <div style={{ fontSize: 28, fontWeight: 800, color, letterSpacing: '-0.02em', lineHeight: 1 }} title={Number(val || 0).toLocaleString()}>
                {formatCompactNumber(val)}
              </div>
              <div style={{ fontSize: 11, color, fontWeight: 600, marginTop: 3 }}>{label}</div>
              {isActive && (
                <div style={{ fontSize: 10, color, marginTop: 4, opacity: 0.7 }}>● Filtering</div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Active filter banner ── */}
      {filter !== 'all' && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '7px 12px', marginBottom: 12,
          background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 8, fontSize: 12,
        }}>
          <span style={{ color: '#1d4ed8', fontWeight: 600 }}>
            🔍 Showing:{' '}
            <strong style={{ textTransform: 'capitalize' }}>
              {filter === 'pass' ? 'Passed' : 'Failed'}
            </strong>{' '}
            — <span title={Number(tenantId ? pageTotal : shown.length).toLocaleString()}>{formatCompactNumber(tenantId ? pageTotal : shown.length)} records</span>
          </span>
          <button
            onClick={() => { setFilter('all'); setPage(1); }}
            style={{
              background: 'none', border: 'none', color: '#3b82f6',
              cursor: 'pointer', fontSize: 12, fontWeight: 700,
              padding: '2px 6px', borderRadius: 4,
            }}
          >
            Clear ✕
          </button>
        </div>
      )}

      {/* ── Compliance rate bar ── */}
      <div style={{ marginBottom: 18, background: '#f8fafc', borderRadius: 10, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Overall Compliance Rate</span>
          <span style={{ fontSize: 18, fontWeight: 800, color: rate >= 80 ? '#15803d' : rate >= 50 ? '#a16207' : '#b91c1c' }}>
            {rate}%
          </span>
        </div>
        <div style={{ height: 10, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${rate}%`,
            background: rate >= 80 ? '#10b981' : rate >= 50 ? '#f59e0b' : '#ef4444',
            borderRadius: 99,
            transition: 'width 0.7s ease',
          }} />
        </div>
      </div>

      {/* ── Rule-by-rule breakdown ── */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
          Rule Breakdown
        </div>
        {ruleBreakdown.map(rb => {
          const ruleRate = totalCount > 0
            ? Math.round((rb.passedCount / totalCount) * 100)
            : 0;
          return (
            <div key={rb.id} style={{ marginBottom: 9 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>{rb.label}</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#15803d', fontWeight: 700 }} title={rb.passedCount.toLocaleString()}>✅ {formatCompactNumber(rb.passedCount)}</span>
                  <span style={{ fontSize: 11, color: '#b91c1c', fontWeight: 700 }} title={rb.failedCount.toLocaleString()}>❌ {formatCompactNumber(rb.failedCount)}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 800, minWidth: 34, textAlign: 'right',
                    color: ruleRate >= 80 ? '#15803d' : ruleRate >= 50 ? '#a16207' : '#b91c1c',
                  }}>{ruleRate}%</span>
                </div>
              </div>
              <div style={{ height: 7, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${ruleRate}%`,
                  background: framework.color, borderRadius: 99,
                  transition: 'width 0.7s ease',
                }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Filter tabs (kept for convenience alongside stat cards) ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[
          { key: 'all', label: `All (${formatCompactNumber(totalCount)})` },
          { key: 'pass', label: `✅ Passed (${formatCompactNumber(passCount)})` },
          { key: 'fail', label: `❌ Failed (${formatCompactNumber(failCount)})` },
        ].map(tab => (
          <button key={tab.key} onClick={() => { setFilter(tab.key); setPage(1); }} style={{
            padding: '5px 14px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: filter === tab.key ? `2px solid ${framework.color}` : '1.5px solid #e2e8f0',
            background: filter === tab.key ? `${framework.color}15` : '#fff',
            color: filter === tab.key ? framework.color : '#64748b',
            transition: 'all 0.12s',
          }}>{tab.label}</button>
        ))}
      </div>

      {/* ── Records table ── */}
      {pageLoading && <div style={{ padding: 8, textAlign: 'center', fontSize: 12, color: '#64748b' }}>Loading compliance records…</div>}
      <div style={{ borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>User</th>
              {framework.rules.map(r => (
                <th key={r.id} style={{ padding: '8px 10px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{r.label}</th>
              ))}
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Result</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr>
                <td colSpan={framework.rules.length + 2} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                  No records
                </td>
              </tr>
            )}
            {/* {shown.slice(0, 50).map((e, i) => ( */}
            {/* <tr key={e.record.id || i} style={{ borderTop: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}> */}
            {shown.map((e, i) => (
              <tr key={e.record.id || i}
                // onClick={() => setSelectedRecord(e.record)}
                onClick={() => {
                  setSelectedRecord(e.record);
                  setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                }}
                style={{ borderTop: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}
                onMouseEnter={ev => ev.currentTarget.style.background = '#eff6ff'}
                onMouseLeave={ev => ev.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}>
                <td style={{ padding: '8px 12px' }}>
                  <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 12 }}>{e.record.username || '—'}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{maskEmail(e.record.email) || ''}</div>
                </td>
                {e.ruleResults.map(rr => (
                  <td key={rr.id} style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <span style={{ fontSize: 14 }}>{rr.passed ? '✅' : '❌'}</span>
                  </td>
                ))}
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <span style={{
                    padding: '2px 9px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                    background: e.passed ? '#dcfce7' : '#fee2e2',
                    color: e.passed ? '#15803d' : '#b91c1c',
                  }}>
                    {e.passed ? 'PASS' : 'FAIL'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tenantId && pageTotal > 20 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 10 }}>
          <button type="button" onClick={() => setPage(value => Math.max(1, value - 1))} disabled={page <= 1 || pageLoading}>Previous</button>
          <span style={{ fontSize: 12, color: '#64748b' }}>Page {page} of {Math.ceil(pageTotal / 20)} · <span title={Number(pageTotal).toLocaleString()}>{formatCompactNumber(pageTotal)} records</span></span>
          <button type="button" onClick={() => setPage(value => Math.min(Math.ceil(pageTotal / 20), value + 1))} disabled={page >= Math.ceil(pageTotal / 20) || pageLoading}>Next</button>
        </div>
      )}

      {selectedRecord && (
        <div ref={detailRef} style={{ marginTop: 16, border: '1.5px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              📄 {selectedRecord.username || maskEmail(selectedRecord.email) || 'Record Detail'}
            </span>
            <button onClick={() => setSelectedRecord(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '2px 6px', borderRadius: 6 }}>✕ Close</button>
          </div>
          <div style={{ padding: '14px 16px' }}>
            <RecordDetail r={selectedRecord} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── COMPLIANCE CARD COMPONENT ────────────────────────────────────────────────
const ComplianceCard = ({ framework, records, stats, monthlyHistory, onCardClick }) => {
  const evaluated = records.map(r => ({
    record: r,
    passed: framework.rules.every(rule => rule.check(r)),
  }));
  const recordCount = Number(stats?.total ?? records.length);
  const passCount = Number(stats?.passed ?? evaluated.filter(e => e.passed).length);
  const failCount = Number(stats?.failed ?? Math.max(0, recordCount - passCount));
  const rate = recordCount > 0 ? Math.round((passCount / recordCount) * 100) : 0;

  const ruleBreakdown = framework.rules.map(rule => ({
    ...rule,
    passedCount: Number(stats?.rules?.[rule.id]?.passed ?? evaluated.filter(e => rule.check(e.record)).length),
  }));
  const maxRule = Math.max(...ruleBreakdown.map(r => r.passedCount), 1);

  const rateColor = rate >= 80 ? '#10b981' : rate >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div className="compliance-card" onClick={() => onCardClick(framework, records, stats)}
      style={{ borderTop: `3px solid ${framework.color}` }}>

      {/* Header */}
      <div className="compliance-card-header" style={{ background: framework.lightBg }}>
        <div className="compliance-card-badge" style={{ background: framework.badgeBg, color: framework.badgeText }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: framework.color, display: 'inline-block' }} />
          {framework.region}
        </div>
        <div className="compliance-card-title">{framework.name}</div>
        <div className="compliance-card-subtitle">{framework.fullName}</div>
      </div>

      {/* Pass / Fail / Rate */}
      <div className="compliance-summary-row">
        <div className="compliance-summary-cell" title={`Pass: ${passCount.toLocaleString()}`}>
          <div className="csc-value" style={{ color: '#10b981' }}>{formatCompactNumber(passCount)}</div>
          <div className="csc-label" style={{ color: '#10b981' }}>Pass</div>
        </div>
        <div className="compliance-summary-cell" title={`Fail: ${failCount.toLocaleString()}`}>
          <div className="csc-value" style={{ color: '#ef4444' }}>{formatCompactNumber(failCount)}</div>
          <div className="csc-label" style={{ color: '#ef4444' }}>Fail</div>
        </div>
        <div className="compliance-summary-cell">
          <div className="csc-value" style={{ color: rateColor }}>{rate}%</div>
          <div className="csc-label" style={{ color: '#64748b' }}>Rate</div>
        </div>
      </div>

      {/* Compliance rate bar */}
      <div style={{ padding: '8px 14px 0', borderTop: '1px solid #f1f5f9' }}>
        <div style={{ height: 6, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${rate}%`,
            background: rateColor, borderRadius: 99,
            transition: 'width 0.8s cubic-bezier(0.34,1.56,0.64,1)',
          }} />
        </div>
      </div>

      {/* Mini bar chart: rule breakdown */}
      <div className="compliance-mini-chart">
        <div className="compliance-mini-chart-title">Rule breakdown</div>
        {ruleBreakdown.map(rb => (
          <div key={rb.id} className="compliance-bar-row">
            <span className="compliance-bar-label" title={rb.label}>{rb.label}</span>
            <div className="compliance-bar-track">
              <div
                className="compliance-bar-fill"
                style={{
                  width: `${Math.round((rb.passedCount / (recordCount || 1)) * 100)}%`,
                  '--bar-w': `${Math.round((rb.passedCount / (recordCount || 1)) * 100)}%`,
                  background: framework.color,
                }}
              />
            </div>
            <span className="compliance-bar-count" title={rb.passedCount.toLocaleString()}>{formatCompactNumber(rb.passedCount)}</span>
          </div>
        ))}
      </div>

      {/* Sparkline history */}
      {monthlyHistory && monthlyHistory.length > 1 && (
        <div className="compliance-sparkline-area">
          {/* <div className="compliance-sparkline-title">6-month pass trend</div> */}
          <MiniSparkline data={monthlyHistory} color={framework.color} />
        </div>
      )}

      {/* Click hint */}
      <div style={{ padding: '8px 14px 12px', display: 'flex', alignItems: 'center', gap: 5, borderTop: '1px solid #f1f5f9' }}>
        <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Click to view pass / fail details</span>
        <span style={{ fontSize: 10, color: framework.color, marginLeft: 'auto', fontWeight: 700 }}>→</span>
      </div>
    </div>
  );
};

// ─── PAGE GUIDE MODAL CONTENT ────────────────────────────────────────────────
function GuideModal({ steps, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1100,
          background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)',
        }}
      />

      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '90%', maxWidth: 540, maxHeight: '82vh',
        background: '#ffffff', borderRadius: 16,
        boxShadow: '0 20px 60px -10px rgba(0,0,0,0.22)',
        zIndex: 1101, display: 'flex', flexDirection: 'column',
        overflow: 'hidden', border: '1.5px solid #cbd5e1',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
          color: '#ffffff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16,
            }}>📘</div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
              Page Guide
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34, height: 34, borderRadius: 9,
              border: '1.5px solid rgba(255,255,255,0.30)',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s ease', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.30)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            aria-label="Close"
          >
            &#10005;
          </button>
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto' }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: 14,
              padding: '14px 0',
              borderBottom: i < steps.length - 1 ? '1px solid #e2e8f0' : 'none',
            }}>
              <div style={{
                flexShrink: 0, width: 30, height: 30, borderRadius: '50%',
                background: '#eff6ff', color: '#1d4ed8', fontWeight: 800, fontSize: 13,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1.5px solid #bfdbfe',
              }}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {s.icon && <span style={{ fontSize: 16 }}>{s.icon}</span>}
                  <span>{s.title}</span>
                </div>
                <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
const ConsentDashboard = ({ user }) => {
  const initialTenantId = user?.tenant_id ? Number(user.tenant_id) : Number(localStorage.getItem('tenant_id')) || null;
  const initialCacheKey = createSessionCacheKey('consent-dashboard', user, {
    tenantId: initialTenantId,
    page: 1,
    pageSize: 10,
    role: 'all',
    dateFrom: '',
    dateTo: '',
    search: '',
    status: null,
    expiringSoon: false,
    expiredOnly: false,
    childOnly: false,
    includeDashboardAggregates: true,
  });
  const [cachedData] = useState(() => readSessionCache(initialCacheKey));
  const [allConsents, setAllConsents] = useState(() => cachedData?.records || []);
  const [dashboardStats, setDashboardStats] = useState(() => cachedData?.filteredStats || cachedData?.stats || {});
  const [recordTotal, setRecordTotal] = useState(() => Number(cachedData?.total || 0));
  const [loading, setLoading] = useState(() => !cachedData);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedRole, setSelectedRole] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [metricModal, setMetricModal] = useState(null);
  const [recordModal, setRecordModal] = useState(null);
  const [chartModal, setChartModal] = useState(null);
  const [complianceModal, setComplianceModal] = useState(null); // { framework, records }
  const [dashboardAggregates, setDashboardAggregates] = useState(() => cachedData?.aggregates || null);
  const [recordPage, setRecordPage] = useState(0);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideStep, setGuideStep] = useState(0);
  const recordPageSize = 10;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // const buildMonthlyData = useCallback((records) => {
  //   const now = new Date();
  //   const last6 = Array.from({ length: 6 }, (_, i) => {
  //     const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
  //     return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: MONTH_LABELS[d.getMonth()] };
  //   });
  //   const mCount = {}, mRev = {};
  //   records.forEach(r => {
  //     if (!r.created_at) return;
  //     const d = new Date(r.created_at);
  //     if (isNaN(d)) return;
  //     const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  //     mCount[key] = (mCount[key] || 0) + 1;
  //     if (/revok/i.test(String(r.status || ''))) mRev[key] = (mRev[key] || 0) + 1;
  //   });
  //   setMonthlyData(last6.map(m => ({ month: m.label, value: mCount[m.key] || 0, key: m.key })));
  //   setMonthlyRevokedData(last6.map(m => ({ month: m.label, value: mRev[m.key] || 0, key: m.key })));

  //   // Build per-framework monthly pass counts for sparklines
  //   const hist = {};
  //   COMPLIANCE_FRAMEWORKS.forEach(fw => {
  //     hist[fw.id] = last6.map(m => {
  //       const monthRecs = records.filter(r => {
  //         if (!r.created_at) return false;
  //         const d = new Date(r.created_at);
  //         return !isNaN(d) && `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === m.key;
  //       });
  //       return monthRecs.filter(r => fw.rules.every(rule => rule.check(r))).length;
  //     });
  //   });
  //   setComplianceHistory(hist);
  // }, []);

  const loadData = useCallback(async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      const tenantId = user?.tenant_id
        ? Number(user.tenant_id)
        : Number(localStorage.getItem('tenant_id')) || null;
      const requestOptions = {
        page: recordPage + 1,
        pageSize: recordPageSize,
        role: selectedRole,
        dateFrom,
        dateTo,
        search: debouncedSearchQuery,
        status: activeFilter?.type === 'status' ? activeFilter.value : null,
        expiringSoon: activeFilter?.type === 'expiringSoon',
        expiredOnly: activeFilter?.type === 'expired',
        childOnly: activeFilter?.type === 'child',
        includeDashboardAggregates: recordPage === 0,
      };
      const result = await fetchConsents(tenantId, requestOptions);
      setAllConsents(result.records);
      if (result.aggregates) setDashboardAggregates(result.aggregates);
      setDashboardStats(result.filteredStats || result.stats);
      setRecordTotal(result.total);
      setLastRefresh(new Date());
      const cacheKey = createSessionCacheKey('consent-dashboard', user, { tenantId, ...requestOptions });
      writeSessionCache(cacheKey, result);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
    // }, [user, buildMonthlyData, recordPage, recordPageSize, selectedRole, dateFrom, dateTo, debouncedSearchQuery, activeFilter]);
  }, [user, recordPage, recordPageSize, selectedRole, dateFrom, dateTo, debouncedSearchQuery, activeFilter]);

  useEffect(() => {
    loadData();
    const onCreated = () => loadData();
    window.addEventListener('consent:created', onCreated);
    return () => window.removeEventListener('consent:created', onCreated);
  }, [loadData]);

  const filtered = allConsents;
  const displayedRecords = filtered;
  const total = Number(dashboardStats.total ?? recordTotal);
  const accepted = Number(dashboardStats.accepted || 0);
  const declined = Number(dashboardStats.declined || 0);
  const revoked = Number(dashboardStats.revoked || 0);
  const reconsent = Number(dashboardStats.reconsent || 0);
  const pending = Number(dashboardStats.pending || 0);
  const expiringSoon = Number(dashboardStats.expiring_soon || 0);
  let childCount = 0, pageExpiredCount = 0;
  filtered.forEach(r => {
    if (r.parent_name || r.child) childCount++;
    const d = computeDaysToExpiry(r);
    if (statusOf(r) === 'accepted' && d !== null && d < 0) pageExpiredCount++;
  });
  const expired = Number(dashboardStats.expired ?? pageExpiredCount);

  // const toSorted = (map) =>
  //   Object.entries(map).sort((a, b) => b[1] - a[1]).map(([k, v], i) => ({ name: k, value: v, color: PALETTE[i % PALETTE.length] }));


  // const chanMap = {}, typeMap = {}, revChanMap = {}, consentTypeMap = {};
  // filtered.forEach(r => {
  //   const ch = channelOf(r);
  //   chanMap[ch] = (chanMap[ch] || 0) + 1;

  //   // id_proof_type — split comma-separated values and count each individually
  //   const rawProof = String(r?.id_proof_type || '').trim();
  //   if (!rawProof) {
  //     typeMap['Not Provided'] = (typeMap['Not Provided'] || 0) + 1;
  //   } else {
  //     const proofParts = rawProof
  //       .split(',')
  //       .map(p => p.trim())
  //       .filter(p => p.length > 0);
  //     // dedupe within same record (avoid counting same proof twice per person)
  //     const uniqueParts = [...new Set(proofParts)];
  //     uniqueParts.forEach(p => {
  //       typeMap[p] = (typeMap[p] || 0) + 1;
  //     });
  //   }

  //   // consent type chart — separate
  //   const ct = String(r?.type || 'Other').trim() || 'Other';
  //   consentTypeMap[ct] = (consentTypeMap[ct] || 0) + 1;

  //   if (statusOf(r) === 'revoked') revChanMap[ch] = (revChanMap[ch] || 0) + 1;
  // });

  // const channelData = toSorted(chanMap);
  // const typeData = toSorted(typeMap);
  // const consentTypeData = toSorted(consentTypeMap);
  // const revokedByChannel = toSorted(revChanMap);

  const withChartColors = (items = []) => items.map((item, index) => ({
    ...item,
    color: PALETTE[index % PALETTE.length],
  }));
  const channelData = withChartColors(dashboardAggregates?.channels);
  const typeData = withChartColors(dashboardAggregates?.id_proofs);
  const consentTypeData = withChartColors(dashboardAggregates?.consent_types);
  const revokedByChannel = withChartColors(dashboardAggregates?.revoked_by_channel);
  const monthlyData = dashboardAggregates?.monthly || [];
  const monthlyRevokedData = dashboardAggregates?.monthly_revoked || [];
  const complianceHistory = dashboardAggregates?.compliance_history || {};



  const handleChannelClick = (d) => setChartModal({
    title: `📡 Channel: ${d.name} — ${formatCompactNumber(d.value)} records`,
    filter: { type: 'channel', value: d.name }
  });

  const handleTypeClick = (d) => setChartModal({
    title: `🪪 ID Proof: ${d.name} — ${formatCompactNumber(d.value)} records`,
    filter: { type: 'id_proof', value: d.name }
  });

  const handleConsentTypeClick = (d) => setChartModal({
    title: `📋 Consent Type: ${d.name} — ${formatCompactNumber(d.value)} records`,
    filter: { type: 'consent_type', value: d.name }
  });

  const handleRevokedChannelClick = (d) => setChartModal({
    title: `🔄 Revoked via ${d.name} — ${formatCompactNumber(d.value)} records`,
    filter: { type: 'channel', value: d.name, status: 'revoked' }
  });

  const handleMonthBarClick = ({ month, consents, revoked: rv, key }) => {
    // const recs = key ? allConsents.filter(r => {
    //   if (!r.created_at) return false;
    //   const d = new Date(r.created_at);
    //   const createDate = d.toISOString().slice(0, 7);
    //   return createDate === key;
    // }) : [];
    setChartModal({
      title: `📅 ${month} — ${formatCompactNumber(consents)} consents, ${formatCompactNumber(rv)} revoked`,
      filter: { type: 'month', value: key }
    });
  };

  const metricCards = [
    { icon: '👥', label: 'Total', value: total, color: '#6366f1', bg: '#eef2ff', filter: null },
    { icon: '✅', label: 'Accepted', value: accepted, color: '#10b981', bg: '#d1fae5', filter: { type: 'status', value: 'Accepted' } },
    { icon: '❌', label: 'Declined', value: declined, color: '#ef4444', bg: '#fee2e2', filter: { type: 'status', value: 'Declined' } },
    { icon: '🔁', label: 'Reconsent', value: reconsent, color: '#f59e0b', bg: '#fef3c7', filter: { type: 'status', value: 'Reconsent' } },
    { icon: '⏳', label: 'Pending', value: pending, color: '#f59e0b', bg: '#fef3c7', filter: { type: 'status', value: 'Pending' } },
    { icon: '🔄', label: 'Revoked', value: revoked, color: '#f97316', bg: '#ffedd5', filter: { type: 'status', value: 'Revoked' } },
    { icon: '🧑', label: 'Adults', value: Math.max(0, total - childCount), color: '#0ea5e9', bg: '#e0f2fe', filter: null },
    { icon: '🧒', label: 'Children', value: childCount, color: '#8b5cf6', bg: '#ede9fe', filter: { type: 'child' } },
    { icon: '🔔', label: 'Expiring', value: expiringSoon, color: '#ec4899', bg: '#fce7f3', filter: { type: 'expiringSoon' } },
    { icon: '📅', label: 'Expired', value: expired, color: '#e11d48', bg: '#ffe4e6', filter: { type: 'expired' } },
  ];

  // const isCardActive = (m) => {
  //   if (!activeFilter && !m.filter) return true;
  //   if (!activeFilter || !m.filter) return false;
  //   return activeFilter.type === m.filter.type && activeFilter.value === m.filter.value;
  // };

  const isCardActive = (m) => {
    if (!activeFilter) return false;
    if (!m.filter) return false;
    return activeFilter.type === m.filter.type && activeFilter.value === m.filter.value;
  };

  const openMetricModal = (filter, label, value) => {
    const same = activeFilter && filter && activeFilter.type === filter.type && activeFilter.value === filter.value;
    const nextFilter = same ? null : filter;
    setActiveFilter(nextFilter);
    setRecordPage(0);
    setMetricModal({
      label,
      value,
      filter: nextFilter,
      status: nextFilter && nextFilter.type === 'status' ? String(nextFilter.value).toLowerCase() : null,
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* TOP BAR */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 49px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 58, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🛡️</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>Consent Dashboard</div>
            {lastRefresh && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Last refreshed: {lastRefresh.toLocaleTimeString()}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div className="tour-target-date-filter" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '6px 8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569' }}>
              <span style={{ fontWeight: 700 }}>From</span>
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setRecordPage(0); }}
                style={{ height: 32, minWidth: 128, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 8px', background: '#fff', color: '#0f172a' }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569' }}>
              <span style={{ fontWeight: 700 }}>To</span>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setRecordPage(0); }}
                style={{ height: 32, minWidth: 128, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 8px', background: '#fff', color: '#0f172a' }} />
            </label>
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); setRecordPage(0); }}
                style={{ height: 32, border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#475569', cursor: 'pointer', padding: '0 10px', fontSize: 12 }}>
                Clear
              </button>
            )}
          </div>

          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13 }}>🔎</span>
            <input
              type="text"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setRecordPage(0); }}
              placeholder="Search by name or email..."
              style={{
                height: 34,
                width: 240,
                border: '1.5px solid #e2e8f0',
                borderRadius: 9,
                background: '#f8fafc',
                padding: '0 34px 0 32px',
                fontSize: 13,
                color: '#0f172a',
                outline: 'none',
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => { setSearchQuery(''); setRecordPage(0); }}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  border: 'none',
                  background: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                ✕
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select className="tour-target-role-select" value={selectedRole} onChange={e => { setSelectedRole(e.target.value); setRecordPage(0); }}
              style={{ height: 34, border: '1.5px solid #e2e8f0', borderRadius: 9, padding: '0 10px', fontSize: 13, color: '#1e293b', background: '#f8fafc', outline: 'none' }}>
              <option value="all">All roles</option>
              <option value="employee">Employees</option>
              <option value="vendor">Vendors & Suppliers</option>
              <option value="partner">Partners</option>
              <option value="customer">Customers</option>
            </select>
            <button className="tour-target-refresh-btn" onClick={() => { loadData(); }} title="Refresh"
              style={{ height: 34, width: 34, border: '1.5px solid #e2e8f0', borderRadius: 9, background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>
              <span style={{ display: 'inline-block', animation: isRefreshing ? 'spin 0.8s linear infinite' : 'none' }}>🔄</span>
            </button>
            <button onClick={() => setGuideOpen(true)} title="Page Guide"
              style={{
                height: 34, border: '1.5px solid #e2e8f0', borderRadius: 9, background: '#f8fafc',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                padding: '0 12px', fontSize: 12, fontWeight: 700, color: '#475569',
              }}>
              <span>📘</span> Page Guide
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding: '21px 2px', maxWidth: 1300, margin: '0 auto' }}>
        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 32, alignItems: 'center', color: '#94a3b8', fontSize: 14 }}>
            <div style={{ fontSize: 28, animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</div>
            <span>Loading consents…</span>
          </div>
        )}

        {!loading && error && (
          <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 10, padding: '14px 18px', marginBottom: 16, color: '#b91c1c', fontSize: 13 }}>
            ⚠️ Failed to load data: {error}
            <button onClick={() => { if (!allConsents.length && !recordTotal) setLoading(true); loadData(); }} style={{ marginLeft: 12, background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}>Retry</button>
          </div>
        )}

        {!loading && (
          <>
            {activeFilter && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '9px 16px', background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 10, fontSize: 13 }}>
                <span style={{ color: '#1d4ed8', fontWeight: 600 }}>🔍 Filtered: <strong>{activeFilter.value || activeFilter.type}</strong> — <span title={Number(recordTotal || 0).toLocaleString()}>{formatCompactNumber(recordTotal)} records</span></span>
                <button onClick={() => { setActiveFilter(null); setRecordPage(0); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Clear ✕</button>
              </div>
            )}

            {/* METRIC CARDS */}
            <div className="tour-target-metric-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(119px, 1fr))', gap: 10, marginBottom: 18 }}>
              {metricCards.map(m => (
                <MetricCard key={m.label} icon={m.icon} label={m.label} value={m.value}
                  color={m.color} bg={m.bg} active={isCardActive(m)}
                  onClick={() => openMetricModal(m.filter, m.label, m.value)} />
              ))}
            </div>

            {/* ─── COMPLIANCE CARDS (GDPR / DPDP / DORA) ─────────────────────── */}
            <div className="compliance-section">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  ⚖️ Regulatory Compliance
                </div>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>Click card to view pass / fail breakdown</span>
              </div>
              <div className="compliance-grid">
                {COMPLIANCE_FRAMEWORKS.map((fw, idx) => (
                  <ComplianceCard
                    key={fw.id}
                    framework={fw}
                    records={filtered}
                    stats={dashboardAggregates?.compliance?.[fw.id]}
                    monthlyHistory={complianceHistory[fw.id]}
                    onCardClick={(framework, records, stats) => setComplianceModal({ framework, records, stats })}
                  />
                ))}
              </div>
            </div>

            {/* CHART ROWS */}
            <div className="tour-target-donut-charts" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <ChartCard title="📡 Collection Channels" hint="Click to view records" onClick={() => setChartModal({ title: '📡 Collection Channels', filter: null })}>
                <DonutChart data={channelData} title="Channels" pageSize={5} onSliceClick={handleChannelClick} />
              </ChartCard>

              <ChartCard title="🪪 ID Proof Types" hint="Click to view records" onClick={() => setChartModal({ title: '🪪 ID Proof Types', filter: null })}>
                <DonutChart data={typeData} title="ID Proofs" pageSize={5} onSliceClick={handleTypeClick} />
              </ChartCard>
            </div>

            <div className="tour-target-bar-charts" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <ChartCard title="🔄 Revoked by Channel" hint="Click to view records" onClick={() => setChartModal({ title: '🔄 Revoked by Channel', filter: { type: 'status', value: 'revoked' } })}>
                <HBarChart data={revokedByChannel} pageSize={5} onBarClick={handleRevokedChannelClick} />
              </ChartCard>
              <ChartCard title="📋 Consent Types Distribution" hint="Click to view records" onClick={() => setChartModal({ title: '📋 Consent Types Distribution', filter: null })}>
                <HBarChart data={consentTypeData} pageSize={8} onBarClick={handleConsentTypeClick} />
              </ChartCard>
            </div>

            {/* MONTHLY CHART */}
            <div className="tour-target-monthly-chart" style={{ marginBottom: 14 }}>
              <ChartCard title="📈 Monthly Consents vs Revoked (last 6 months)" hint="Click to view records" onClick={() => setChartModal({ title: '📈 Monthly Consents vs Revoked', filter: null })}>
                <MonthlyChart consentData={monthlyData} revokedData={monthlyRevokedData} onBarClick={handleMonthBarClick} />
              </ChartCard>
            </div>

            {/* ALL RECORDS TABLE */}
            <div className="tour-target-all-records" style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>All Records</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }} title={Number(recordTotal || 0).toLocaleString()}>{formatCompactNumber(recordTotal)} records · click row for details</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {activeFilter && <span style={{ fontSize: 12, background: '#eff6ff', color: '#3b82f6', padding: '3px 10px', borderRadius: 99, fontWeight: 600 }}>{activeFilter.value || activeFilter.type}</span>}
                  {Math.ceil(recordTotal / recordPageSize) > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Page {recordPage + 1}/{Math.ceil(recordTotal / recordPageSize)}</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <NavBtn onClick={() => setRecordPage(p => Math.max(0, p - 1))} disabled={recordPage === 0} label="‹" />
                        <NavBtn onClick={() => setRecordPage(p => Math.min(Math.ceil(recordTotal / recordPageSize) - 1, p + 1))} disabled={recordPage >= Math.ceil(recordTotal / recordPageSize) - 1} label="›" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <RecordTable records={displayedRecords} onRowClick={r => setRecordModal(r)} perPage={recordPageSize} page={recordPage} serverPaged />
            </div>
          </>
        )}
      </div>

      {/* METRIC MODAL */}
      <Modal isOpen={!!metricModal} onClose={() => setMetricModal(null)}
        title={metricModal ? `${metricModal.label}${metricModal.value !== undefined ? ` — ${formatCompactNumber(metricModal.value)} records` : ''}` : ''}
        size="lg">
        {metricModal && <PagedMetricContent
          key={`${metricModal.label}:${metricModal.status || 'all'}:${metricModal.filter?.type || 'all'}`}
          tenantId={user?.tenant_id ? Number(user.tenant_id) : Number(localStorage.getItem('tenant_id')) || null}
          filter={metricModal.filter}
          role={selectedRole}
          dateFrom={dateFrom}
          dateTo={dateTo}
          search={debouncedSearchQuery}
          initialStats={dashboardStats}
          initialTotal={metricModal.value}
        />}
      </Modal>

      {/* CHART POPUP MODAL */}
      <Modal isOpen={!!chartModal} onClose={() => setChartModal(null)} title={chartModal?.title || ''} size="lg">
        {chartModal && <PagedMetricContent
          key={`chartModal:${chartModal.title}:${JSON.stringify(chartModal.filter || {})}`}
          tenantId={user?.tenant_id ? Number(user.tenant_id) : Number(localStorage.getItem('tenant_id')) || null}
          filter={chartModal.filter}
          role={selectedRole}
          dateFrom={dateFrom}
          dateTo={dateTo}
          search={debouncedSearchQuery}
          initialStats={dashboardStats}
        />}
      </Modal>

      {/* RECORD DETAIL MODAL */}
      <Modal isOpen={!!recordModal} onClose={() => setRecordModal(null)}
        title={`Consent Ref #${recordModal?.encrypted_id || recordModal?.public_id || (recordModal?.id ? `CNS-${String(recordModal.id).padStart(8, '0')}` : '')} — ${recordModal?.username || maskEmail(recordModal?.email) || ''}`} size="md">
        {recordModal && <RecordDetail r={recordModal} />}
      </Modal>

      {/* COMPLIANCE DETAIL MODAL */}
      <Modal
        isOpen={!!complianceModal}
        onClose={() => setComplianceModal(null)}
        title={complianceModal ? `⚖️ ${complianceModal.framework.name} — ${complianceModal.framework.fullName}` : ''}
        size="lg">
        {complianceModal && (
          <ComplianceModalContent
            framework={complianceModal.framework}
            records={complianceModal.records}
            stats={complianceModal.stats}
            tenantId={user?.tenant_id ? Number(user.tenant_id) : Number(localStorage.getItem('tenant_id')) || null}
            role={selectedRole}
            dateFrom={dateFrom}
            dateTo={dateTo}
            search={debouncedSearchQuery}
          />
        )}
      </Modal>
      {/* PAGE GUIDE MODAL */}
      {guideOpen && <GuideModal steps={GUIDE_STEPS} onClose={() => setGuideOpen(false)} />}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes barGrow { from { width: 0%; } to { width: var(--bar-w, 100%); } }
        * { box-sizing: border-box; }
        input:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 2px #6366f120; }
        .compliance-section { margin-bottom: 14px; animation: fadeInUp 0.45s ease both; }
        .compliance-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
        .compliance-card {
          background: #fff; border: 1.5px solid #e2e8f0; border-radius: 16px;
          overflow: hidden; cursor: pointer;
          transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
          animation: fadeInUp 0.4s ease both;
        }
        .compliance-card:hover { transform: translateY(-3px); box-shadow: 0 8px 28px rgba(0,0,0,0.09); }
        .compliance-card:active { transform: translateY(-1px); }
        .compliance-card-header { padding: 14px 16px 12px; position: relative; overflow: hidden; }
        .compliance-card-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 9px 3px 6px; border-radius: 99px; font-size: 10px;
          font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 8px;
        }
        .compliance-card-title { font-size: 21px; font-weight: 800; color: #0f172a; letter-spacing: -0.03em; line-height: 1; margin: 0 0 2px; }
        .compliance-card-subtitle { font-size: 11px; color: #64748b; font-weight: 500; margin: 0; }
        .compliance-summary-row { display: flex; gap: 0; border-top: 1px solid #f1f5f9; }
        .compliance-summary-cell { flex: 1; padding: 10px 14px; border-right: 1px solid #f1f5f9; text-align: center; }
        .compliance-summary-cell:last-child { border-right: none; }
        .compliance-summary-cell .csc-value { font-size: 22px; font-weight: 800; line-height: 1; letter-spacing: -0.02em; }
        .compliance-summary-cell .csc-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; margin-top: 2px; }
        .compliance-mini-chart { padding: 10px 14px 12px; border-top: 1px solid #f1f5f9; }
        .compliance-mini-chart-title { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 8px; }
        .compliance-bar-row { display: flex; align-items: center; gap: 7px; margin-bottom: 5px; font-size: 11px; }
        .compliance-bar-row:last-child { margin-bottom: 0; }
        .compliance-bar-label { width: 52px; color: #475569; font-weight: 500; flex-shrink: 0; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .compliance-bar-track { flex: 1; height: 7px; background: #f1f5f9; border-radius: 99px; overflow: hidden; }
        .compliance-bar-fill { height: 100%; border-radius: 99px; animation: barGrow 0.7s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay: 0.2s; }
        .compliance-bar-count { min-width: 28px; width: auto; text-align: right; color: #1e293b; font-weight: 700; font-size: 10px; flex-shrink: 0; white-space: nowrap; }
        .compliance-sparkline-area { padding: 0 14px 14px; border-top: 1px solid #f1f5f9; }
        .compliance-sparkline-title { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.07em; padding-top: 10px; margin-bottom: 6px; }
        .dashboard-chart-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; margin-bottom: 14px; }
        @media (max-width: 840px) {
          .compliance-grid { grid-template-columns: 1fr; }
          .dashboard-chart-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default ConsentDashboard;